import { Capacitor, registerPlugin } from '@capacitor/core';
import { abortable } from './abortable';
import { confirmSystemAction } from './systemConfirmation';
import { getSessionIdentity, subscribeSessionChanges } from '../hooks/useSessionIdentity';
import translations from '../imagePickerTranslations';

const picker = registerPlugin('WiwImagePicker');
const settings = registerPlugin('WiwDeviceSettings');
export const NATIVE_IMAGE_PICK_TIMEOUT = 200000;
export const MAX_NATIVE_IMAGE_BYTES = 4 * 1024 * 1024;
const failure = code => Object.assign(new Error(code), { code });
const cancelled = () => new DOMException('Image selection cancelled', 'AbortError');

export const canPickNativeImage = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
  && Boolean(Capacitor.isPluginAvailable?.('WiwImagePicker'));

// The native side exports a bounded portable raster, never a filesystem path.
// Keep using handleImageUpload afterwards for the existing 1080px/512KiB limits.
export function nativeImageFile(result) {
  if (!['image/jpeg', 'image/png'].includes(result?.mimeType) || typeof result.base64 !== 'string'
    || !result.base64.length || result.base64.length > Math.ceil(MAX_NATIVE_IMAGE_BYTES / 3) * 4
    || result.base64.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(result.base64)) throw failure('IMAGE_UNREADABLE');
  let binary;
  try { binary = atob(result.base64); } catch { throw failure('IMAGE_UNREADABLE'); }
  if (!binary.length || binary.length > MAX_NATIVE_IMAGE_BYTES) throw failure('IMAGE_UNREADABLE');
  const bytes = Uint8Array.from(binary, value => value.charCodeAt(0));
  return new File([bytes], result.mimeType === 'image/jpeg' ? 'selected-photo.jpg' : 'selected-photo.png', { type: result.mimeType });
}

export async function pickNativeImage({ signal, language = 'en' } = {}) {
  if (!canPickNativeImage()) throw failure('IMAGE_PICK_UNAVAILABLE');
  if (signal?.aborted) throw cancelled();
  const identity = getSessionIdentity();
  const copy = translations[String(language).toLowerCase().split(/[-_]/)[0]] || translations.en;
  const requestId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const value = Math.floor(Math.random() * 16);
    return (character === 'x' ? value : (value & 3) | 8).toString(16);
  });
  const controller = new AbortController();
  let timedOut = false;
  const stop = () => {
    controller.abort();
    try { Promise.resolve(picker.cancel({ requestId })).catch(() => {}); } catch { /* The caller must still be released. */ }
  };
  signal?.addEventListener('abort', stop, { once: true });
  const unsubscribe = subscribeSessionChanges(() => { if (identity !== getSessionIdentity()) stop(); });
  const timer = setTimeout(() => { timedOut = true; stop(); }, NATIVE_IMAGE_PICK_TIMEOUT);
  const current = () => !controller.signal.aborted && identity === getSessionIdentity();
  const wait = promise => abortable(promise, controller.signal, cancelled());
  try {
    const result = await wait(picker.pickImage({ requestId, title: copy.title, library: copy.library,
      camera: copy.camera, files: copy.files, cancelLabel: copy.cancelLabel }));
    if (!current()) throw cancelled();
    if (result?.status === 'cancelled') return null;
    if (result?.status === 'permission-restricted') throw failure('IMAGE_CAMERA_RESTRICTED');
    if (result?.status === 'permission-denied') {
      const confirmed = await wait(confirmSystemAction({ title: copy.deniedTitle, message: copy.denied,
        confirmLabel: copy.openSettings, cancelLabel: copy.cancelLabel }));
      if (!current()) throw cancelled();
      if (confirmed) {
        try { await wait(settings.openMediaSettings()); }
        catch (error) { if (!current()) throw error; throw failure('IMAGE_SETTINGS_UNAVAILABLE'); }
      }
      // Returning from Settings requires another deliberate tap. Never open the camera automatically.
      return null;
    }
    if (result?.status !== 'selected') throw failure('IMAGE_UNREADABLE');
    return nativeImageFile(result);
  } catch (error) {
    if (timedOut) throw failure('IMAGE_PICK_TIMEOUT');
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', stop);
    unsubscribe();
  }
}

export const imageSelectionErrorKey = error => error?.code === 'IMAGE_CAMERA_RESTRICTED' ? 'imagePicker:restricted'
  : error?.code === 'IMAGE_SETTINGS_UNAVAILABLE' ? 'imagePicker:settingsUnavailable'
    : error?.code === 'IMAGE_UNREADABLE' ? 'brand:imageFormatError' : 'brand:imageError';
